/**
 * Lazy profit badge — fetches profit on demand (button click) to avoid
 * batting N requests when admin scrolls through the orders list.
 */
import { useState } from 'react';
import axios from 'axios';
import { TrendingUp, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OrderProfitBadge = ({ orderId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (data || loading) {
      setOpen((o) => !o);
      return;
    }
    setLoading(true);
    setOpen(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/orders/${orderId}/profit`);
      setData(res.data);
    } catch {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const margin = data?.margin_pct ?? 0;
  const marginColor =
    margin >= 50 ? 'text-green-400'
    : margin >= 30 ? 'text-[color:var(--color-primary)]'
    : margin >= 10 ? 'text-orange-400'
    : 'text-red-400';

  return (
    <div className="py-3 border-t border-white/5" data-testid={`profit-${orderId}`}>
      <button
        type="button"
        onClick={load}
        className="flex items-center gap-2 text-xs text-zinc-400 hover:text-[color:var(--color-primary)] transition-colors"
        data-testid={`profit-toggle-${orderId}`}
      >
        <TrendingUp className="w-3 h-3" />
        {open ? 'Masquer' : 'Voir'} le bénéfice
      </button>

      {open && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {loading && <p className="text-zinc-500 col-span-full">Calcul…</p>}
          {!loading && data?.error && (
            <p className="text-red-400 col-span-full">Erreur lors du calcul du bénéfice</p>
          )}
          {!loading && data && !data.error && (
            <>
              <div>
                <p className="text-zinc-500 uppercase tracking-wider mb-0.5">Revenu</p>
                <p className="text-white text-base">{data.revenue.toFixed(2)}€</p>
              </div>
              <div>
                <p className="text-zinc-500 uppercase tracking-wider mb-0.5">Coût</p>
                <p className="text-zinc-300 text-base">{data.cost.toFixed(2)}€</p>
              </div>
              <div>
                <p className="text-zinc-500 uppercase tracking-wider mb-0.5">Bénéfice</p>
                <p className={`text-base ${marginColor}`}>{data.profit.toFixed(2)}€</p>
              </div>
              <div>
                <p className="text-zinc-500 uppercase tracking-wider mb-0.5">Marge</p>
                <p className={`text-base ${marginColor}`}>{data.margin_pct}%</p>
              </div>
              {data.missing_costs?.length > 0 && (
                <div className="col-span-full flex items-start gap-2 text-amber-400 mt-1">
                  <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span>
                    Prix d'achat manquant pour {data.missing_costs.length} produit{data.missing_costs.length > 1 ? 's' : ''} —{' '}
                    {data.missing_costs.slice(0, 3).map(m => m.name).join(', ')}
                    {data.missing_costs.length > 3 && '…'}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
